import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ComposedChart, Area, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ExternalLink, Info, Search, Smartphone } from 'lucide-react';
import './App.css';
import AlgoNodeRewardsImg from './AlgoNodeRewards.png';
import RetiImg from './Reti.png';
import PFPImg from './PFP.jpg';
import ComingSoon from './ComingSoon';
import GrowthPanels, { FaqPopup, SwapPopup } from './GrowthPanels';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const INDEXER_BASE_URL =
  process.env.REACT_APP_INDEXER_BASE_URL ||
  'https://mainnet-idx.4160.nodely.dev';
const FALLBACK_INDEXER_BASE_URLS = [
  INDEXER_BASE_URL,
  'https://mainnet-idx.algonode.cloud',
].filter((value, index, values) => value && values.indexOf(value) === index);
const ALGOD_BASE_URL =
  process.env.REACT_APP_ALGOD_BASE_URL || 'https://mainnet-api.algonode.cloud';
const ENABLE_NFD_LOOKUPS = process.env.REACT_APP_ENABLE_NFD_LOOKUPS === 'true';
const ENABLE_VALIDATOR_SNAPSHOT =
  process.env.REACT_APP_ENABLE_VALIDATOR_SNAPSHOT === 'true';
const BLOCK_REWARD_SENDER = 'Y76M3MSY6DKBRHBL7C3NNDXGS5IIMQVQVUAB6MP4XEMMGVF2QWNPL226CA';
const ROUND_DURATION_SECONDS = 3;
const CURRENCY_OPTIONS = {
  USD: { code: 'USD', label: 'US Dollar', shortLabel: 'USD', locale: 'en-US', fallbackRate: 1, digits: 2 },
  EUR: { code: 'EUR', label: 'Euro', shortLabel: 'EUR', locale: 'de-DE', fallbackRate: 0.874, digits: 2 },
  GBP: { code: 'GBP', label: 'British Pound', shortLabel: 'GBP', locale: 'en-GB', fallbackRate: 0.749, digits: 2 },
  CAD: { code: 'CAD', label: 'Canadian Dollar', shortLabel: 'CAD', locale: 'en-CA', fallbackRate: 1.42, digits: 2 },
  AUD: { code: 'AUD', label: 'Australian Dollar', shortLabel: 'AUD', locale: 'en-AU', fallbackRate: 1.442, digits: 2 },
  JPY: { code: 'JPY', label: 'Japanese Yen', shortLabel: 'JPY', locale: 'ja-JP', fallbackRate: 161.39, digits: 0 },
  MXN: { code: 'MXN', label: 'Mexican Peso', shortLabel: 'MXN', locale: 'es-MX', fallbackRate: 17.48, digits: 2 },
  BRL: { code: 'BRL', label: 'Brazilian Real', shortLabel: 'BRL', locale: 'pt-BR', fallbackRate: 5.18, digits: 2 },
  CHF: { code: 'CHF', label: 'Swiss Franc', shortLabel: 'CHF', locale: 'de-CH', fallbackRate: 0.804, digits: 2 },
  SEK: { code: 'SEK', label: 'Swedish Krona', shortLabel: 'SEK', locale: 'sv-SE', fallbackRate: 9.65, digits: 2 },
  NOK: { code: 'NOK', label: 'Norwegian Krone', shortLabel: 'NOK', locale: 'nb-NO', fallbackRate: 9.83, digits: 2 },
};
const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_OPTIONS);
const FALLBACK_CURRENCY_RATES = SUPPORTED_CURRENCIES.reduce(
  (rates, code) => ({
    ...rates,
    [code]: CURRENCY_OPTIONS[code].fallbackRate,
  }),
  {},
);

function App() {
  const [poolData, setPoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mempoolData, setMempoolData] = useState(null);
  const [mempoolError, setMempoolError] = useState(null);
  const [revealedAddresses, setRevealedAddresses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showHowToPopup, setShowHowToPopup] = useState(false);
  const [showFaqPopup, setShowFaqPopup] = useState(false);
  const closeFaqPopup = useCallback(() => setShowFaqPopup(false), []);
  const [showSwapPopup, setShowSwapPopup] = useState(false);
  const closeSwapPopup = useCallback(() => setShowSwapPopup(false), []);
  const [useRetroBackground, setUseRetroBackground] = useState(false);
  const [nfdCache, setNfdCache] = useState({});
  const [validatorSnapshot, setValidatorSnapshot] = useState({
    lastBlockRound: null,
    lastBlockTime: null,
    proposer: null,
    estimatedIntervalMinutes: null,
    status: 'idle',
  });
  const [currency, setCurrency] = useState('USD');
  const [currencyRates, setCurrencyRates] = useState(FALLBACK_CURRENCY_RATES);
  const [currencyRatesUpdatedAt, setCurrencyRatesUpdatedAt] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const [stakeAmountPopup, setStakeAmountPopup] = useState(null);
  const [lastBlockPulse, setLastBlockPulse] = useState(false);
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [chartTapState, setChartTapState] = useState({ lastKey: null, hiddenKey: null });
  const prevLastBlockRoundRef = useRef(null);
  const nfdLookupsInFlightRef = useRef(new Set());
  const validatorSnapshotSupportedRef = useRef(null);
  const lastGoodStakerDataRef = useRef(null);

  const hasUsableStakerRows = useCallback((data) => {
    const rows = Array.isArray(data?.staker_rewards) ? data.staker_rewards : [];
    if (rows.length === 0) {
      return false;
    }

    const performance = data?.performance ?? {};
    const rewardFlow =
      Number(performance.avg_staker_rewards_per_day_30d_algo) ||
      Number(performance.avg_staker_rewards_per_day_algo) ||
      Number(performance.avg_rewards_per_day_30d_algo) ||
      Number(performance.avg_rewards_per_day_algo) ||
      Number(performance.total_rewards_algo) ||
      0;
    if (rewardFlow > 0) {
      return rows.some((row) => Number(row?.daily_rewards_algo) > 0);
    }

    return true;
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD', {
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(`Exchange rate HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const rates = data?.rates ?? {};
        const nextRates = SUPPORTED_CURRENCIES.reduce((acc, code) => {
          const rate = Number(rates[code]);
          acc[code] = Number.isFinite(rate) ? rate : FALLBACK_CURRENCY_RATES[code];
          return acc;
        }, {});

        if (isMounted && data?.result === 'success') {
          setCurrencyRates(nextRates);
          setCurrencyRatesUpdatedAt(data?.time_last_update_utc || new Date().toISOString());
        }
      } catch (err) {
        console.error('Error fetching currency exchange rates:', err);
      }
    };

    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 30 * 60 * 1000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => {
      setIsMobileView(event.matches);
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
    }
    setIsMobileView(mediaQuery.matches);
    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobileView && stakeAmountPopup) {
      setStakeAmountPopup(null);
    }
  }, [isMobileView, stakeAmountPopup]);

  useEffect(() => {
    setStakeAmountPopup(null);
  }, [poolData]);

  const isComingSoonPage = window.location.pathname === '/coming-soon';
  const lastPayoutRound = poolData?.pool_info?.global_state?.lastPayout;
  const blocksPerDayForInterval =
    poolData?.performance?.avg_blocks_per_day_7d ??
    poolData?.performance?.avg_blocks_per_day_30d ??
    poolData?.performance?.avg_blocks_per_day ??
    null;
  const validatorAddress = poolData?.validator_address;

  const fetchPoolData = useCallback(async () => {
    try {
      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/pool-analysis`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const hasStakerRows = hasUsableStakerRows(data);
      if (hasStakerRows) {
        lastGoodStakerDataRef.current = {
          stakers: data.stakers,
          staker_rewards: data.staker_rewards,
          performance: data.performance,
          total_staked_algo: data.total_staked_algo,
          algo_price: data.algo_price,
        };
        setPoolData(data);
      } else if (lastGoodStakerDataRef.current) {
        const lastGood = lastGoodStakerDataRef.current;
        setPoolData({
          ...data,
          stakers: lastGood.stakers,
          staker_rewards: lastGood.staker_rewards,
          performance: lastGood.performance || data.performance,
          total_staked_algo: data?.total_staked_algo || lastGood.total_staked_algo,
          algo_price: data?.algo_price || lastGood.algo_price,
          staker_data_status: 'stale',
          staker_data_message:
            'Showing the last good staker list while fresh staker data catches up.',
        });
      } else {
        setPoolData({
          ...data,
          stakers: [],
          staker_rewards: [],
          staker_data_status: 'syncing',
          staker_data_message:
            data?.staker_data_message ||
            'Staker data is syncing from the indexer. Rewards and pool metrics are still live.',
        });
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pool data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [hasUsableStakerRows]);

  useEffect(() => {
    fetchPoolData();
    const interval = setInterval(fetchPoolData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchPoolData]);

  const fetchMempoolData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/mempool?limit=12`, {
        cache: 'no-store',
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data?.errors?.[0] || data?.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      setMempoolData(data);
      setMempoolError(null);
    } catch (err) {
      console.error('Error fetching mempool data:', err);
      setMempoolError(err.message);
    }
  }, []);

  useEffect(() => {
    let interval = null;
    const start = () => {
      if (interval) return;
      fetchMempoolData();
      interval = setInterval(fetchMempoolData, 5000);
    };
    const stop = () => {
      clearInterval(interval);
      interval = null;
    };
    const handleVisibility = () => (document.hidden ? stop() : start());

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchMempoolData]);

  const currencyConfig = useMemo(
    () =>
      SUPPORTED_CURRENCIES.reduce((config, code) => {
        const option = CURRENCY_OPTIONS[code];
        const liveRate = Number(currencyRates[code]);
        config[code] = {
          ...option,
          rate: Number.isFinite(liveRate) ? liveRate : option.fallbackRate,
        };
        return config;
      }, {}),
    [currencyRates],
  );

  const convertCurrencyAmount = (amount) => {
    if (amount === null || amount === undefined) {
      return 0;
    }
    const numericAmount = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(numericAmount)) {
      return 0;
    }
    const rate = currencyConfig[currency]?.rate ?? 1;
    return numericAmount * rate;
  };

  const formatCurrency = (amount, fractionDigits = null) => {
    const config = currencyConfig[currency] ?? currencyConfig.USD;
    const convertedAmount = convertCurrencyAmount(amount);
    const digits = typeof fractionDigits === 'number' ? fractionDigits : config.digits ?? 2;
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(convertedAmount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toFixed(2);
  };

  const formatAverageValue = (value, fractionDigits = 2) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  const toFiniteNumber = (value) => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const firstFiniteNumber = (...values) => {
    for (const value of values) {
      const parsed = toFiniteNumber(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    return 0;
  };

  const formatAlgoAmount = (value, fractionDigits = 2) => {
    const numericValue = toFiniteNumber(value) ?? 0;
    const absoluteValue = Math.abs(numericValue);
    const digits = absoluteValue > 0 && absoluteValue < 1 ? Math.max(fractionDigits, 4) : fractionDigits;

    return numericValue.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const formatIntervalMinutes = (minutes) => {
    if (!minutes || !isFinite(minutes)) {
      return 'N/A';
    }

    if (minutes >= 1440) {
      const days = minutes / 1440;
      return `${days.toFixed(1)} day${days >= 2 ? 's' : ''}`;
    }

    if (minutes >= 60) {
      const hours = minutes / 60;
      return `${hours.toFixed(1)} hr${hours >= 2 ? 's' : ''}`;
    }

    return `${minutes.toFixed(1)} min`;
  };

  const formatRelativeTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }

    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);

    if (diffMinutes < 1) {
      return 'just now';
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
    }

    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hr${diffHours === 1 ? '' : 's'} ago`;
    }

    const diffDays = Math.round(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const handleStakeAmountClick = (staker) => {
    if (!isMobileView) {
      return;
    }
    setStakeAmountPopup((prev) =>
      prev?.address === staker.address
        ? null
        : {
            address: staker.address,
            algoAmount: staker.staked_amount_algo,
            usdAmount: staker.staked_amount_usd,
          },
    );
  };

  const dailyStats = poolData?.performance?.daily_stats;

  const {
    normalizedDailyStats,
    averageRewards,
    averageBlocks,
    latestRewards,
    latestStakerRewards,
    latestBlocks,
    rewardsLast7Average,
    blocksLast7Average,
    rewardsTrendPct,
    blocksTrendPct,
    bestRewards,
    bestBlocks,
  } = useMemo(() => {
    const statsArray = Array.isArray(dailyStats) ? dailyStats : [];

    if (statsArray.length === 0) {
      return {
        normalizedDailyStats: statsArray,
        averageRewards: null,
        averageBlocks: null,
        latestRewards: null,
        latestStakerRewards: null,
        latestBlocks: null,
        rewardsLast7Average: null,
        blocksLast7Average: null,
        rewardsTrendPct: null,
        blocksTrendPct: null,
        bestRewards: null,
        bestBlocks: null,
      };
    }

    let rewardsSum = 0;
    let rewardsCount = 0;
    let blocksSum = 0;
    let blocksCount = 0;
    let bestRewardsValue = null;
    let bestBlocksValue = null;

    const normalizedStats = statsArray.map((entry) => {
      const rewardsValue = toFiniteNumber(entry?.rewards_algo);
      const stakerRewardsValue = toFiniteNumber(entry?.staker_rewards_algo);
      const commissionValue = toFiniteNumber(entry?.commission_algo);
      const rewardsUsdValue = toFiniteNumber(entry?.rewards_usd);
      const stakerRewardsUsdValue = toFiniteNumber(entry?.staker_rewards_usd);
      const commissionUsdValue = toFiniteNumber(entry?.commission_usd);
      const blocksValue = toFiniteNumber(entry?.blocks);
      const isPartialDay = entry?.is_partial === true;

      if (rewardsValue !== null && !isPartialDay) {
        rewardsSum += rewardsValue;
        rewardsCount += 1;
        bestRewardsValue = bestRewardsValue === null ? rewardsValue : Math.max(bestRewardsValue, rewardsValue);
      }

      if (blocksValue !== null && !isPartialDay) {
        blocksSum += blocksValue;
        blocksCount += 1;
        bestBlocksValue = bestBlocksValue === null ? blocksValue : Math.max(bestBlocksValue, blocksValue);
      }

      return {
        ...entry,
        rewards_algo: rewardsValue ?? 0,
        staker_rewards_algo: stakerRewardsValue ?? 0,
        commission_algo: commissionValue ?? 0,
        rewards_usd: rewardsUsdValue ?? 0,
        staker_rewards_usd: stakerRewardsUsdValue ?? 0,
        commission_usd: commissionUsdValue ?? 0,
        blocks: blocksValue ?? 0,
      };
    });

    const averageForRange = (entries, key) => {
      let sum = 0;
      let count = 0;

      entries.forEach((entry) => {
        const value = toFiniteNumber(entry?.[key]);
        if (value !== null) {
          sum += value;
          count += 1;
        }
      });

      return count > 0 ? sum / count : null;
    };

    const calculateTrendPct = (currentAverage, previousAverage) => {
      if (
        currentAverage === null ||
        previousAverage === null ||
        !Number.isFinite(currentAverage) ||
        !Number.isFinite(previousAverage) ||
        previousAverage === 0
      ) {
        return null;
      }

      return ((currentAverage - previousAverage) / previousAverage) * 100;
    };

    // Today's in-progress day is charted but left out of the summary math so
    // it matches the backend's completed-day 7D/30D figures.
    const completedStats = normalizedStats.filter((entry) => entry.is_partial !== true);
    const latestEntry = completedStats[completedStats.length - 1] ?? null;
    const last7Stats = completedStats.slice(-7);
    const previous7Stats = completedStats.slice(-14, -7);
    const rewards7Average = averageForRange(last7Stats, 'rewards_algo');
    const previousRewards7Average = averageForRange(previous7Stats, 'rewards_algo');
    const blocks7Average = averageForRange(last7Stats, 'blocks');
    const previousBlocks7Average = averageForRange(previous7Stats, 'blocks');

    return {
      normalizedDailyStats: normalizedStats,
      averageRewards: rewardsCount ? rewardsSum / rewardsCount : null,
      averageBlocks: blocksCount ? blocksSum / blocksCount : null,
      latestRewards: latestEntry ? toFiniteNumber(latestEntry.rewards_algo) : null,
      latestStakerRewards: latestEntry ? toFiniteNumber(latestEntry.staker_rewards_algo) : null,
      latestBlocks: latestEntry ? toFiniteNumber(latestEntry.blocks) : null,
      rewardsLast7Average: rewards7Average,
      blocksLast7Average: blocks7Average,
      rewardsTrendPct: calculateTrendPct(rewards7Average, previousRewards7Average),
      blocksTrendPct: calculateTrendPct(blocks7Average, previousBlocks7Average),
      bestRewards: bestRewardsValue,
      bestBlocks: bestBlocksValue,
    };
  }, [dailyStats]);

  const averageRewardsLineColor = useRetroBackground ? '#f472b6' : '#f97316';
  const averageBlocksLineColor = averageRewardsLineColor;

  const formatChartDate = (value, format = 'short') => {
    if (!value) {
      return '';
    }

    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString('en-US', {
      month: format === 'long' ? 'long' : 'short',
      day: 'numeric',
      ...(format === 'long' ? { year: 'numeric' } : {}),
    });
  };

  const formatTrendPercent = (value) => {
    if (value === null || !Number.isFinite(value)) {
      return 'n/a';
    }

    const sign = value > 0 ? '+' : '';
    return `${sign}${formatAverageValue(value, 1)}%`;
  };

  const getTrendClass = (value) => {
    if (value === null || !Number.isFinite(value)) {
      return 'neutral';
    }

    return value >= 0 ? 'positive' : 'negative';
  };

  const getChartTooltipKey = (chartId, label) => {
    if (label === null || label === undefined) {
      return null;
    }

    return `${chartId}:${label}`;
  };

  const handleChartTap = (chartId, chartState) => {
    const tooltipKey = getChartTooltipKey(
      chartId,
      chartState?.activeLabel ?? chartState?.activeTooltipIndex,
    );

    if (!tooltipKey) {
      return;
    }

    setChartTapState((prev) => {
      const shouldHide = prev.lastKey === tooltipKey && prev.hiddenKey !== tooltipKey;
      return {
        lastKey: tooltipKey,
        hiddenKey: shouldHide ? tooltipKey : null,
      };
    });
  };

  const renderRewardsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null;
    }

    if (chartTapState.hiddenKey === getChartTooltipKey('rewards', label)) {
      return null;
    }

    const entry = payload[0]?.payload ?? {};
    const rewardsAlgo = toFiniteNumber(entry.rewards_algo);
    const stakerRewardsAlgo = toFiniteNumber(entry.staker_rewards_algo);
    const rewardsUsd = toFiniteNumber(entry.rewards_usd);

    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-date">{formatChartDate(label, 'long')}</div>
        <div className="chart-tooltip-row">
          <span>Gross rewards</span>
          <strong>{rewardsAlgo !== null ? `${formatAlgoAmount(rewardsAlgo, 2)} ALGO` : 'N/A'}</strong>
        </div>
        <div className="chart-tooltip-row accent-green">
          <span>To stakers</span>
          <strong>{stakerRewardsAlgo !== null ? `${formatAlgoAmount(stakerRewardsAlgo, 2)} ALGO` : 'N/A'}</strong>
        </div>
        <div className="chart-tooltip-foot">
          {rewardsUsd !== null ? formatCurrency(rewardsUsd) : 'Value unavailable'}
        </div>
      </div>
    );
  };

  const renderBlocksTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null;
    }

    if (chartTapState.hiddenKey === getChartTooltipKey('blocks', label)) {
      return null;
    }

    const entry = payload[0]?.payload ?? {};
    const blocks = toFiniteNumber(entry.blocks);
    const rewardsAlgo = toFiniteNumber(entry.rewards_algo);

    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-date">{formatChartDate(label, 'long')}</div>
        <div className="chart-tooltip-row accent-blue">
          <span>Blocks proposed</span>
          <strong>{blocks !== null ? formatAverageValue(blocks, 0) : 'N/A'}</strong>
        </div>
        <div className="chart-tooltip-row">
          <span>Rewards earned</span>
          <strong>{rewardsAlgo !== null ? `${formatAlgoAmount(rewardsAlgo, 2)} ALGO` : 'N/A'}</strong>
        </div>
      </div>
    );
  };



  // Fetch NFD names for all staker addresses when pool data loads
  useEffect(() => {
    if (!ENABLE_NFD_LOOKUPS) {
      return;
    }

    if (!poolData?.staker_rewards || poolData.staker_rewards.length === 0) {
      return;
    }

    const addressesToLookup = poolData.staker_rewards
      .map((staker) => staker.address)
      .filter((address, index, addresses) => address && addresses.indexOf(address) === index)
      .filter(
        (address) =>
          nfdCache[address] === undefined &&
          !nfdLookupsInFlightRef.current.has(address),
      );

    if (addressesToLookup.length === 0) {
      return;
    }

    addressesToLookup.forEach((address) => {
      nfdLookupsInFlightRef.current.add(address);
    });

    const fetchNFDs = async () => {
      await Promise.all(
        addressesToLookup.map(async (address) => {
          try {
            const response = await fetch(
              `https://api.nf.domains/nfd/lookup?address=${encodeURIComponent(address)}`,
            );

            if (response.status === 404) {
              setNfdCache((prev) => ({ ...prev, [address]: null }));
              return;
            }

            if (!response.ok) {
              throw new Error(
                `Failed NFD lookup (${response.status} ${response.statusText})`,
              );
            }

            const raw = await response.text();

            if (!raw) {
              setNfdCache((prev) => ({ ...prev, [address]: null }));
              return;
            }

            let parsed = null;
            try {
              parsed = JSON.parse(raw);
            } catch (parseErr) {
              console.error('Error parsing NFD response:', parseErr);
              setNfdCache((prev) => ({ ...prev, [address]: null }));
              return;
            }

            const normalizedAddress = address.toUpperCase();
            let resolvedName = null;

            if (parsed) {
              if (typeof parsed.name === 'string') {
                resolvedName = parsed.name;
              } else if (
                parsed[normalizedAddress] &&
                typeof parsed[normalizedAddress].name === 'string'
              ) {
                resolvedName = parsed[normalizedAddress].name;
              } else {
                const firstEntry = Object.values(parsed)[0];
                if (
                  firstEntry &&
                  typeof firstEntry === 'object' &&
                  typeof firstEntry.name === 'string'
                ) {
                  resolvedName = firstEntry.name;
                }
              }
            }

            setNfdCache((prev) => ({ ...prev, [address]: resolvedName }));
          } catch (err) {
            console.error('Error looking up NFD:', err);
            setNfdCache((prev) => ({ ...prev, [address]: null }));
          } finally {
            nfdLookupsInFlightRef.current.delete(address);
          }
        }),
      );
    };

    fetchNFDs();
  }, [poolData, nfdCache]);

  useEffect(() => {
    if (!validatorAddress) {
      return;
    }

    const controllers = new Set();
    let intervalId = null;

    const fetchValidatorStatus = async () => {
      const controller = new AbortController();
      controllers.add(controller);

      let computedEstimatedInterval = null;

      try {
        setValidatorSnapshot((prev) => ({
          ...prev,
          status: prev.status === 'ready' ? 'ready' : 'loading',
        }));

        let effectiveRound = null;
        let candidateTimestampMs = null;
        let proposer = validatorAddress;
        let accountStakeMicroAlgo = null;

        if (
          ENABLE_VALIDATOR_SNAPSHOT &&
          API_BASE_URL &&
          validatorSnapshotSupportedRef.current !== false
        ) {
          try {
            const params = new URLSearchParams();
            if (validatorAddress) {
              params.set('address', validatorAddress);
            }
            if (blocksPerDayForInterval) {
              params.set('blocksPerDay', String(blocksPerDayForInterval));
            }
            if (lastPayoutRound) {
              params.set('lastPayoutRound', String(lastPayoutRound));
            }

            const snapshotUrl = `${API_BASE_URL}/api/validator-snapshot${
              params.toString() ? `?${params.toString()}` : ''
            }`;
            const snapshotRes = await fetch(snapshotUrl, {
              signal: controller.signal,
            });

            if (snapshotRes.ok) {
              validatorSnapshotSupportedRef.current = true;
              const snapshot = await snapshotRes.json();
              if (snapshot && typeof snapshot === 'object') {
                const lastBlockTimeMs =
                  typeof snapshot.lastBlockTimeMs === 'number'
                    ? snapshot.lastBlockTimeMs
                    : typeof snapshot.lastBlockTime === 'string'
                      ? Date.parse(snapshot.lastBlockTime)
                      : null;
                const lastBlockDate =
                  typeof lastBlockTimeMs === 'number' &&
                  Number.isFinite(lastBlockTimeMs)
                    ? new Date(lastBlockTimeMs)
                    : null;
                const resolvedInterval =
                  typeof snapshot.estimatedIntervalMinutes === 'number' &&
                  Number.isFinite(snapshot.estimatedIntervalMinutes)
                    ? snapshot.estimatedIntervalMinutes
                    : null;
                const fallbackInterval =
                  resolvedInterval ??
                  (blocksPerDayForInterval && blocksPerDayForInterval > 0
                    ? 1440 / blocksPerDayForInterval
                    : null);

                setValidatorSnapshot((prev) => ({
                  ...prev,
                  lastBlockRound: snapshot.lastBlockRound ?? prev.lastBlockRound,
                  lastBlockTime: lastBlockDate ?? prev.lastBlockTime,
                  proposer: snapshot.proposer ?? validatorAddress,
                  estimatedIntervalMinutes:
                    typeof fallbackInterval === 'number' &&
                    Number.isFinite(fallbackInterval)
                      ? fallbackInterval
                      : prev.estimatedIntervalMinutes,
                  status:
                    snapshot.status ||
                    (lastBlockDate ? 'ready' : prev.lastBlockRound ? 'ready' : 'error'),
                }));
                return;
              }
            } else if (snapshotRes.status === 404) {
              validatorSnapshotSupportedRef.current = false;
            } else if (snapshotRes.status !== 404) {
              const errorText = await snapshotRes.text().catch(() => null);
              console.error(
                `Validator snapshot error (${snapshotRes.status} ${snapshotRes.statusText})`,
                errorText,
              );
              setValidatorSnapshot((prev) => ({
                ...prev,
                status: prev.lastBlockRound ? 'ready' : 'error',
              }));
              return;
            }
          } catch (snapshotErr) {
            if (snapshotErr.name !== 'AbortError') {
              console.error('Error fetching validator snapshot:', snapshotErr);
            }
          }
        }

        const blockHeaderCandidates = FALLBACK_INDEXER_BASE_URLS.map(
          (baseUrl) =>
            `${baseUrl}/v2/block-headers?proposers=${encodeURIComponent(validatorAddress)}&limit=1`,
        );
        const rewardTransactionCandidates = FALLBACK_INDEXER_BASE_URLS.map(
          (baseUrl) =>
            `${baseUrl}/v2/transactions?address=${encodeURIComponent(validatorAddress)}&sender=${BLOCK_REWARD_SENDER}&tx-type=pay&address-role=receiver&limit=10`,
        );

        try {
          let accountData = null;
          const accountCandidates = FALLBACK_INDEXER_BASE_URLS.map(
            (baseUrl) =>
              `${baseUrl}/v2/accounts/${encodeURIComponent(validatorAddress)}`,
          );

          for (const accountUrl of accountCandidates) {
            const accountRes = await fetch(accountUrl, { signal: controller.signal });

            if (accountRes.ok) {
              accountData = await accountRes.json();
              break;
            }

            console.error(
              `Indexer account error (${accountRes.status} ${accountRes.statusText})`,
            );
          }

          if (!accountData) {
            const algodAccountRes = await fetch(
              `${ALGOD_BASE_URL}/v2/accounts/${encodeURIComponent(validatorAddress)}`,
              { signal: controller.signal },
            );
            if (algodAccountRes.ok) {
              accountData = await algodAccountRes.json();
            }
          }

          if (accountData) {
            const accountInfo = accountData?.account ?? accountData;
            const participation = accountInfo.participation ?? {};
            const lastProposedRoundRaw =
              accountInfo['last-proposed'] ??
              participation['last-proposed'] ??
              participation.lastProposed ??
              participation.last_proposed ??
              participation.last_proposal ??
              null;
            const lastProposedRound = toFiniteNumber(lastProposedRoundRaw);

            if (lastProposedRound !== null) {
              effectiveRound = lastProposedRound;
            }

            const balanceFields = [
              accountInfo['amount-without-pending-rewards'],
              accountInfo.amountWithoutPendingRewards,
              accountInfo['amount'],
              accountInfo.amount,
            ];
            const resolvedStake = balanceFields
              .map((value) => toFiniteNumber(value))
              .find((value) => value !== null && value >= 0);
            if (resolvedStake != null) {
              accountStakeMicroAlgo = resolvedStake;
            }
          }
        } catch (accountErr) {
          if (accountErr.name !== 'AbortError') {
            console.error('Error fetching validator account:', accountErr);
          }
        }

        if (accountStakeMicroAlgo && accountStakeMicroAlgo > 0) {
          try {
            const stakeRes = await fetch(
              'https://afmetrics.api.nodely.io/v1/realtime/participation/online',
              { signal: controller.signal },
            );

            if (stakeRes.ok) {
              const stakeJson = await stakeRes.json();
              const rawStake =
                stakeJson?.stake_micro_algo ??
                stakeJson?.stakeMicroAlgo ??
                stakeJson?.stake ??
                null;
              const totalOnlineStakeMicroAlgo =
                typeof rawStake === 'string' ? Number(rawStake) : rawStake;

              if (
                typeof totalOnlineStakeMicroAlgo === 'number' &&
                Number.isFinite(totalOnlineStakeMicroAlgo) &&
                totalOnlineStakeMicroAlgo > 0
              ) {
                const stakeShare = accountStakeMicroAlgo / totalOnlineStakeMicroAlgo;
                if (stakeShare > 0) {
                  computedEstimatedInterval =
                    (ROUND_DURATION_SECONDS / stakeShare) / 60;
                }
              }
            } else {
              console.error(
                `Online stake error (${stakeRes.status} ${stakeRes.statusText})`,
              );
            }
          } catch (stakeErr) {
            if (stakeErr.name !== 'AbortError') {
              console.error('Error fetching total online stake:', stakeErr);
            }
          }
        }

        if (
          (!computedEstimatedInterval || !Number.isFinite(computedEstimatedInterval)) &&
          blocksPerDayForInterval &&
          blocksPerDayForInterval > 0
        ) {
          computedEstimatedInterval = 1440 / blocksPerDayForInterval;
        }

        if (!effectiveRound && lastPayoutRound) {
          const parsedPayoutRound = toFiniteNumber(lastPayoutRound);
          if (parsedPayoutRound !== null) {
            effectiveRound = parsedPayoutRound;
          }
        }

        // Fetch block reward transactions (most reliable for latest round)
        for (const url of rewardTransactionCandidates) {
          if (effectiveRound && candidateTimestampMs) {
            break;
          }
          try {
            const txRes = await fetch(url, { signal: controller.signal });
            if (!txRes.ok) {
              continue;
            }

            const txData = await txRes.json();
            const transactions = Array.isArray(txData.transactions)
              ? txData.transactions
              : [];

            for (const txn of transactions) {
              if (
                txn &&
                txn['tx-type'] === 'pay' &&
                (txn.sender ?? txn['sender']) === BLOCK_REWARD_SENDER
              ) {
                const confirmed = toFiniteNumber(
                  txn['confirmed-round'] ??
                    txn.confirmedRound ??
                    txn['round'] ??
                    null,
                );
                if (confirmed !== null) {
                  if (
                    typeof effectiveRound !== 'number' ||
                    confirmed > effectiveRound
                  ) {
                    effectiveRound = confirmed;
                  }
                }

                const roundTime = toFiniteNumber(
                  txn['round-time'] ??
                    txn.roundTime ??
                    txn['timestamp'] ??
                    null,
                );
                if (roundTime !== null && roundTime > 0) {
                  const timestampMs = roundTime * 1000;
                  if (
                    typeof candidateTimestampMs !== 'number' ||
                    timestampMs > candidateTimestampMs
                  ) {
                    candidateTimestampMs = timestampMs;
                  }
                }
                break;
              }
            }
          } catch (txErr) {
            if (txErr.name === 'AbortError') {
              throw txErr;
            }
            console.error('Error fetching block reward transaction:', txErr);
          }
        }

        // Fall back to block headers if rewards unavailable
        if (!effectiveRound || !candidateTimestampMs) {
          for (const url of blockHeaderCandidates) {
            try {
              const headerRes = await fetch(url, { signal: controller.signal });
              if (!headerRes.ok) {
                continue;
              }
              const headerData = await headerRes.json();
              const headerBlock = Array.isArray(headerData.blocks)
                ? headerData.blocks[0]
                : null;

              if (!headerBlock) {
                continue;
              }

              const headerRound = toFiniteNumber(
                headerBlock.round ?? headerBlock['round'] ?? null,
              );
              if (headerRound !== null) {
                if (
                  typeof effectiveRound !== 'number' ||
                  headerRound > effectiveRound
                ) {
                  effectiveRound = headerRound;
                }
              }

              const headerTimestamp = toFiniteNumber(
                headerBlock.timestamp ?? headerBlock['timestamp'] ?? null,
              );
              if (headerTimestamp !== null && headerTimestamp > 0) {
                const timestampMs = headerTimestamp * 1000;
                if (
                  typeof candidateTimestampMs !== 'number' ||
                  timestampMs > candidateTimestampMs
                ) {
                  candidateTimestampMs = timestampMs;
                }
              }

              const headerProposer =
                headerBlock.proposer ?? headerBlock['proposer'] ?? null;
              if (typeof headerProposer === 'string' && headerProposer.length > 0) {
                proposer = headerProposer;
              }

              if (effectiveRound && candidateTimestampMs) {
                break;
              }
            } catch (headerErr) {
              if (headerErr.name === 'AbortError') {
                throw headerErr;
              }
              console.error('Error fetching block header data:', headerErr);
            }
          }
        }

        if (!effectiveRound) {
          setValidatorSnapshot((prev) => ({
            ...prev,
            estimatedIntervalMinutes:
              typeof computedEstimatedInterval === 'number' &&
              Number.isFinite(computedEstimatedInterval)
                ? computedEstimatedInterval
                : prev.estimatedIntervalMinutes,
            status: prev.lastBlockRound ? 'ready' : 'error',
          }));
          return;
        }

        let blockTimestampMs = candidateTimestampMs;

        try {
          let blockData = null;
          for (const baseUrl of FALLBACK_INDEXER_BASE_URLS) {
            const blockRes = await fetch(`${baseUrl}/v2/blocks/${effectiveRound}`, {
              signal: controller.signal,
            });
            if (blockRes.ok) {
              blockData = await blockRes.json();
              break;
            }
          }

          if (!blockData) {
            const algodBlockRes = await fetch(
              `${ALGOD_BASE_URL}/v2/blocks/${effectiveRound}`,
              { signal: controller.signal },
            );
            if (algodBlockRes.ok) {
              blockData = await algodBlockRes.json();
            }
          }

          if (blockData) {
            const inner = blockData.block ?? blockData;
            const blockTimestamp = toFiniteNumber(
              blockData.timestamp ??
                blockData.block?.timestamp ??
                inner.timestamp ??
                inner.ts,
            );

            if (blockTimestamp !== null && blockTimestamp > 0) {
              blockTimestampMs = blockTimestamp * 1000;
            }

            const blockProposer =
              typeof blockData.proposer === 'string'
                ? blockData.proposer
                : blockData.block && typeof blockData.block.proposer === 'string'
                  ? blockData.block.proposer
                  : null;
            if (typeof blockProposer === 'string' && blockProposer.length > 0) {
              proposer = blockProposer;
            }
          }
        } catch (blockErr) {
          if (blockErr.name === 'AbortError') {
            throw blockErr;
          }
          console.error('Error fetching validator block:', blockErr);
        }

        const blockDate =
          typeof blockTimestampMs === 'number' && Number.isFinite(blockTimestampMs)
            ? new Date(blockTimestampMs)
            : null;

        setValidatorSnapshot((prev) => ({
          ...prev,
          lastBlockRound: effectiveRound ?? prev.lastBlockRound,
          lastBlockTime: blockDate ?? prev.lastBlockTime,
          proposer,
          estimatedIntervalMinutes:
            typeof computedEstimatedInterval === 'number' &&
            Number.isFinite(computedEstimatedInterval)
              ? computedEstimatedInterval
              : prev.estimatedIntervalMinutes,
          status: blockDate ? 'ready' : prev.lastBlockRound ? 'ready' : 'error',
        }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching validator data:', err);
          setValidatorSnapshot((prev) => ({
            ...prev,
            estimatedIntervalMinutes:
              typeof computedEstimatedInterval === 'number' &&
              Number.isFinite(computedEstimatedInterval)
                ? computedEstimatedInterval
                : prev.estimatedIntervalMinutes,
            status: prev.lastBlockRound ? 'ready' : 'error',
          }));
        }
      } finally {
        controllers.delete(controller);
      }
    };

    fetchValidatorStatus();
    intervalId = setInterval(fetchValidatorStatus, 60000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      controllers.forEach((ctrl) => ctrl.abort());
      controllers.clear();
    };
  }, [validatorAddress, blocksPerDayForInterval, lastPayoutRound]);

  useEffect(() => {
    const round = validatorSnapshot.lastBlockRound;
    if (round == null || typeof round !== 'number') return;
    const prev = prevLastBlockRoundRef.current;
    if (prev !== null && prev !== round) {
      setLastBlockPulse(true);
      const t = setTimeout(() => setLastBlockPulse(false), 2500);
      prevLastBlockRoundRef.current = round;
      return () => clearTimeout(t);
    }
    prevLastBlockRoundRef.current = round;
  }, [validatorSnapshot.lastBlockRound]);

  useEffect(() => {
    const interval = setInterval(() => setCountdownNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  const lastBlockDisplay = (() => {
    if (validatorSnapshot.status === 'loading') {
      return 'Loading...';
    }

    const hasValidTime =
      validatorSnapshot.lastBlockTime instanceof Date &&
      !Number.isNaN(validatorSnapshot.lastBlockTime?.getTime());

    if (!hasValidTime) {
      if (validatorSnapshot.status === 'error') {
        return 'Unavailable';
      }
      return validatorSnapshot.status === 'idle' ? 'Loading...' : 'N/A';
    }

    const relative = formatRelativeTime(validatorSnapshot.lastBlockTime);
    return relative || validatorSnapshot.lastBlockTime.toLocaleTimeString();
  })();

  const lastBlockTitle = (() => {
    const hasValidTime =
      validatorSnapshot.lastBlockTime instanceof Date &&
      !Number.isNaN(validatorSnapshot.lastBlockTime?.getTime());

    if (!hasValidTime) {
      return undefined;
    }

    const segments = [
      `Recorded at ${validatorSnapshot.lastBlockTime.toLocaleString()}`,
    ];

    if (validatorSnapshot.lastBlockRound) {
      segments.unshift(
        typeof validatorSnapshot.lastBlockRound === 'number'
          ? `Round #${validatorSnapshot.lastBlockRound}`
          : `Round ${validatorSnapshot.lastBlockRound}`,
      );
    }

    if (validatorSnapshot.proposer) {
      segments.push(`Proposer ${validatorSnapshot.proposer}`);
    }

    return segments.join(' | ');
  })();

  const hasIntervalValue =
    typeof validatorSnapshot.estimatedIntervalMinutes === 'number' &&
    isFinite(validatorSnapshot.estimatedIntervalMinutes);
  const estimatedIntervalDisplay = hasIntervalValue
    ? formatIntervalMinutes(validatorSnapshot.estimatedIntervalMinutes)
    : validatorSnapshot.status === 'loading' || validatorSnapshot.status === 'idle'
      ? 'Loading...'
      : 'N/A';
  const estimatedIntervalTitle =
    hasIntervalValue
      ? `${validatorSnapshot.estimatedIntervalMinutes.toFixed(
          1,
        )} minutes between proposals (stake share estimate)`
      : undefined;

  const nextBlockCountdownLabel = (() => {
    const hasTime =
      validatorSnapshot.lastBlockTime instanceof Date &&
      !Number.isNaN(validatorSnapshot.lastBlockTime?.getTime());
    if (
      !hasTime ||
      !hasIntervalValue ||
      validatorSnapshot.estimatedIntervalMinutes <= 0
    ) {
      return null;
    }
    const nextBlockMs =
      validatorSnapshot.lastBlockTime.getTime() +
      validatorSnapshot.estimatedIntervalMinutes * 60 * 1000;
    const remainingMs = nextBlockMs - countdownNow;
    if (remainingMs <= 0) return 'Any moment';
    if (remainingMs < 60 * 1000) return 'Soon';
    const mins = Math.ceil(remainingMs / (60 * 1000));
    return `~${mins} min`;
  })();

  if (isComingSoonPage) {
    return <ComingSoon />;
  }

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedStakers = () => {
    if (!poolData?.staker_rewards) return [];

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    
    let filteredStakers = poolData.staker_rewards.filter((staker) => {
      if (!normalizedSearchTerm) {
        return true;
      }

      const addressMatches = staker.address?.toLowerCase().includes(normalizedSearchTerm);
      const nfdMatches = nfdCache[staker.address]?.toLowerCase().includes(normalizedSearchTerm);
      return addressMatches || nfdMatches;
    });

    if (sortConfig.key) {
      filteredStakers.sort((a, b) => {
        let aValue, bValue;
        
        // Map the sort keys to actual field names
        switch(sortConfig.key) {
          case 'balance':
            aValue = a.staked_amount_algo;
            bValue = b.staked_amount_algo;
            break;
          case 'pool_pct':
            aValue = a.stake_percentage;
            bValue = b.stake_percentage;
            break;
          case 'daily_rewards':
            aValue = firstFiniteNumber(
              a.estimated_rewards_per_day_algo,
              a.daily_rewards_algo,
              a.daily_staking_rewards_algo,
            );
            bValue = firstFiniteNumber(
              b.estimated_rewards_per_day_algo,
              b.daily_rewards_algo,
              b.daily_staking_rewards_algo,
            );
            break;
          case 'weekly_rewards':
            aValue = firstFiniteNumber(
              a.estimated_rewards_7d_algo,
              a.staking_rewards_7d_algo,
              a.rewards_7d_algo,
              a.weekly_rewards_algo,
              firstFiniteNumber(a.estimated_rewards_per_day_algo, a.daily_rewards_algo) * 7,
            );
            bValue = firstFiniteNumber(
              b.estimated_rewards_7d_algo,
              b.staking_rewards_7d_algo,
              b.rewards_7d_algo,
              b.weekly_rewards_algo,
              firstFiniteNumber(b.estimated_rewards_per_day_algo, b.daily_rewards_algo) * 7,
            );
            break;
          case 'monthly_rewards':
            aValue = firstFiniteNumber(
              a.estimated_rewards_30d_algo,
              a.staking_rewards_30d_algo,
              a.rewards_30d_algo,
              a.monthly_rewards_algo,
              firstFiniteNumber(a.estimated_rewards_per_day_algo, a.daily_rewards_algo) * 30,
            );
            bValue = firstFiniteNumber(
              b.estimated_rewards_30d_algo,
              b.staking_rewards_30d_algo,
              b.rewards_30d_algo,
              b.monthly_rewards_algo,
              firstFiniteNumber(b.estimated_rewards_per_day_algo, b.daily_rewards_algo) * 30,
            );
            break;
          case 'yearly_rewards':
            aValue = firstFiniteNumber(
              a.estimated_rewards_1y_algo,
              a.yearly_staking_rewards_algo,
              a.yearly_rewards_algo,
              firstFiniteNumber(a.estimated_rewards_per_day_algo, a.daily_rewards_algo) * 365,
            );
            bValue = firstFiniteNumber(
              b.estimated_rewards_1y_algo,
              b.yearly_staking_rewards_algo,
              b.yearly_rewards_algo,
              firstFiniteNumber(b.estimated_rewards_per_day_algo, b.daily_rewards_algo) * 365,
            );
            break;
          case 'total_earned':
            aValue = a.total_earned_algo ?? 0;
            bValue = b.total_earned_algo ?? 0;
            break;
          default:
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }
        
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredStakers;
  };



  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2 className="loading-title">Loading Reti Pool Data</h2>
          <p className="loading-text">Fetching the latest information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">Connection Error</h2>
          <p className="error-text">Unable to fetch pool data: {error}</p>
          <button onClick={fetchPoolData} className="error-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalStaked = poolData?.total_staked_algo ? poolData.total_staked_algo * 1000000 : 0;
  const totalStakers = poolData?.stakers?.length || 0;
  const avgStake = totalStakers > 0 ? totalStaked / totalStakers / 1000000 : 0;
  const parsedAlgoPrice = toFiniteNumber(poolData?.algo_price);
  const algoPrice = parsedAlgoPrice ?? 0;
  const totalValueUsd = (totalStaked / 1000000) * algoPrice;
  const selectedCurrencyConfig = currencyConfig[currency] ?? currencyConfig.USD;
  const selectedCurrencyRate = toFiniteNumber(selectedCurrencyConfig?.rate) ?? 1;
  const currencyRateDigits = selectedCurrencyRate >= 100 ? 2 : selectedCurrencyRate >= 10 ? 3 : 4;
  const currencyRateLabel =
    currency === 'USD'
      ? 'USD selected'
      : `1 USD = ${formatAverageValue(selectedCurrencyRate, currencyRateDigits)} ${currency}`;
  const currencyRatesUpdatedLabel = currencyRatesUpdatedAt
    ? `Updated ${formatRelativeTime(new Date(currencyRatesUpdatedAt)) || 'recently'}`
    : 'Using fallback FX';
  const stakerRewardRows = Array.isArray(poolData?.staker_rewards) ? poolData.staker_rewards : [];
  const totalEarnedAlgo = stakerRewardRows.reduce(
    (sum, staker) => sum + firstFiniteNumber(staker.total_earned_algo),
    0,
  );
  const netStakerApy30d = firstFiniteNumber(
    poolData?.performance?.estimated_apy_30d,
    poolData?.performance?.estimated_apy,
  );
  const grossApy30d = toFiniteNumber(poolData?.performance?.estimated_gross_apy_30d);
  const dailyStakerRewardsAlgo = firstFiniteNumber(
    poolData?.performance?.avg_staker_rewards_per_day_30d_algo,
    poolData?.performance?.avg_staker_rewards_per_day_algo,
  );
  const dailyStakerRewardsUsd = firstFiniteNumber(
    poolData?.performance?.avg_staker_rewards_per_day_30d_usd,
    dailyStakerRewardsAlgo * algoPrice,
  );
  const sortedStakers = getSortedStakers();
  const stakerDataIsStale = poolData?.staker_data_status === 'stale';
  const stakerListIsEmpty = stakerRewardRows.length === 0;
  const stakerSyncMessage =
    poolData?.staker_data_message ||
    'Staker data is syncing from the indexer. Rewards and pool metrics are still live.';
  const mempoolTransactions = Array.isArray(mempoolData?.transactions)
    ? mempoolData.transactions
    : [];
  const mempoolRecentTransactions = Array.isArray(mempoolData?.recent_transactions)
    ? mempoolData.recent_transactions
    : [];
  const mempoolShowingRecentFlow = mempoolTransactions.length === 0 && mempoolRecentTransactions.length > 0;
  const mempoolDisplayTransactions = mempoolShowingRecentFlow
    ? mempoolRecentTransactions
    : mempoolTransactions;
  const mempoolTotalTransactions = firstFiniteNumber(
    mempoolData?.total_transactions,
    mempoolTransactions.length,
  );
  const mempoolNodeLabel = mempoolData?.algod_node
    ? mempoolData.algod_node.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : 'connecting';
  const mempoolUpdatedLabel = mempoolData?.fetched_at
    ? formatRelativeTime(new Date(mempoolData.fetched_at))
    : 'waiting';
  const mempoolFeedIsLive = mempoolData?.status === 'live' && !mempoolError;

  const formatMempoolAmount = (transaction) => {
    const amountAlgo = toFiniteNumber(transaction?.amount_algo);
    if (amountAlgo !== null) {
      return `${formatAlgoAmount(amountAlgo, 4)} ALGO`;
    }
    const assetAmount = toFiniteNumber(transaction?.asset_amount);
    if (assetAmount !== null) {
      return `${formatAverageValue(assetAmount, 0)} units`;
    }
    if (transaction?.type === 'axfer') {
      return 'Asset transfer';
    }
    if (transaction?.app_id) {
      return `App ${transaction.app_id}`;
    }
    return 'Pending';
  };

  const formatMatrixFlowLabel = (transaction, index) => {
    const flowStatus = transaction?.flow_status === 'confirmed' ? 'CONFIRMED' : 'PENDING';
    const amountLabel = formatMempoolAmount(transaction);
    const typeLabel = transaction?.type_label || 'Transaction';
    const summary = transaction?.summary || `Network transaction ${index + 1}`;
    return `${flowStatus} :: ${typeLabel} :: ${amountLabel} :: ${summary}`;
  };

  const matrixFlowLines = mempoolDisplayTransactions.length > 0
    ? mempoolDisplayTransactions.slice(0, 10).map(formatMatrixFlowLabel)
    : [
        mempoolError
          ? `RECONNECTING :: ${mempoolError}`
          : `LIVE :: ${formatAverageValue(mempoolTotalTransactions, 0) || '0'} pending :: ${mempoolNodeLabel}`,
      ];
  const matrixFlowMode = mempoolShowingRecentFlow ? 'confirmed-flow' : 'pending-flow';
  const matrixFlowStatus = mempoolError
    ? 'RECONNECTING'
    : mempoolShowingRecentFlow
      ? 'PENDING CLEAR / CONFIRMED FLOW'
      : `${formatAverageValue(mempoolTotalTransactions, 0) || '0'} PENDING`;
  const poolDataUpdatedAt = (() => {
    const raw = poolData?.last_updated;
    if (typeof raw !== 'string' || !raw) return null;
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
    const parsed = new Date(hasZone ? raw : `${raw}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  })();
  const poolDataUpdatedLabel = poolDataUpdatedAt ? formatRelativeTime(poolDataUpdatedAt) : 'syncing';
  const matrixFlowSource = `${mempoolNodeLabel} / updated ${mempoolUpdatedLabel}`;

  return (
    <div className={`dashboard${useRetroBackground ? ' retro-theme' : ''}`}>
      <div
        className={`network-matrix-bg ${mempoolFeedIsLive ? 'has-live-flow' : 'is-reconnecting'} ${matrixFlowMode}`}
        aria-hidden="true"
      >
        <div className="matrix-streams">
          {Array.from({ length: 30 }).map((_, index) => (
            <span
              key={`stream-${index + 1}`}
              className="matrix-stream"
              style={{
                '--matrix-left': `${(index * 17) % 100}%`,
                '--matrix-duration': `${15 + (index % 8) * 1.7}s`,
                '--matrix-delay': `${index * -0.9}s`,
              }}
            />
          ))}
        </div>
        <div className="matrix-live-status">
          <span data-flow={matrixFlowStatus} />
          <span data-flow={matrixFlowSource} />
        </div>
        <div className="matrix-live-flow">
          {matrixFlowLines.map((line, index) => (
            <span
              key={`flow-${index}`}
              className={`matrix-flow-line flow-${(index % 10) + 1}`}
              data-flow={line}
            />
          ))}
        </div>
        <div className="network-packet-field">
          {Array.from({ length: 34 }).map((_, index) => (
            <span key={`packet-${index + 1}`} className={`network-packet packet-${index + 1}`} />
          ))}
        </div>
      </div>
      {useRetroBackground && (
        <div className="retro-ambient">
          <span className="retro-orb orb-one" />
          <span className="retro-orb orb-two" />
          <span className="retro-orb orb-three" />
          <span className="retro-orb orb-four" />
        </div>
      )}
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src={PFPImg} alt="Profile" className="logo-icon-img" />
            <div className="logo-text">
              <h1>Poorslugspool.algo</h1>
              <p>Real-time Algorand staking analytics</p>
              <p className="enrollment-status">Enrolled in XGov</p>
            </div>
          </div>
          <div className="header-right">
            <div className="partner-logos">
              <a 
                href="https://algonoderewards.com/E3L6U7D3Y7EMNO6FGADEL3X4GVFRRBIXJ47PF5NG6PNS6WVWC2J6UMDEQA?hideBalance=false&theme=system&statsPanelTheme=indigo"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-logo"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <img src={AlgoNodeRewardsImg} alt="Algo Node Rewards" className="partner-img" />
                <span className="partner-name">Algo Node Rewards</span>
              </a>
              <a
                href="https://reti.nodely.io/validators/19"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-logo"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <img src={RetiImg} alt="Reti Pool" className="partner-img" />
                <span className="partner-name">Reti Pool</span>
              </a>
            </div>
            <div className="header-actions-buttons">
              <button onClick={() => setShowSwapPopup(true)} className="how-to-btn">
                Swap
              </button>
              <button onClick={() => setShowHowToPopup(true)} className="how-to-btn">
                How to stake
              </button>
              <button onClick={() => setShowFaqPopup(true)} className="refresh-btn">
                FAQ
              </button>
            </div>
          </div>
        </div>
        <div className="header-status">
          <div className="header-actions header-status-inner">
            <div className="live-indicator">
              <div className="live-dot"></div>
              <span style={{ color: '#34d399', fontSize: '0.875rem', fontWeight: '500' }}>Live</span>
            </div>
            <div className="live-metrics">
              <div className={`live-metric ${lastBlockPulse ? 'live-metric-pulse' : ''}`}>
                <span className="live-metric-label">Last block</span>
                <span className="live-metric-value" title={lastBlockTitle || undefined}>
                  {lastBlockDisplay}
                </span>
                {nextBlockCountdownLabel && (
                  <span className="live-metric-next" title="Estimated time until next block">
                    Next in {nextBlockCountdownLabel}
                  </span>
                )}
              </div>
              <div className="live-metric">
                <span className="live-metric-label">Estimated interval</span>
                <span className="live-metric-value" title={estimatedIntervalTitle || undefined}>
                  {estimatedIntervalDisplay}
                </span>
              </div>
            </div>
            <div className="currency-selector">
              <span className="currency-selector-label">Currency</span>
              <div className="currency-select-wrap">
                <select
                  className="currency-select"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                  aria-label="Currency selector"
                >
                  {SUPPORTED_CURRENCIES.map((code) => {
                    const option = currencyConfig[code];
                    if (!option) {
                      return null;
                    }
                    return (
                      <option key={code} value={code}>
                        {option.shortLabel} - {option.label}
                      </option>
                    );
                  })}
                </select>
                <span className="currency-select-arrow" aria-hidden="true">v</span>
              </div>
              <span className="currency-rate">
                {currencyRateLabel} | {currencyRatesUpdatedLabel}
              </span>
            </div>
            <div className="theme-toggle">
              <input
                id="retro-toggle"
                type="checkbox"
                className="theme-toggle-input"
                checked={useRetroBackground}
                onChange={() => setUseRetroBackground((prev) => !prev)}
                aria-label="Toggle retro background"
              />
              <label htmlFor="retro-toggle" className="theme-toggle-label">
                <span className="theme-toggle-track">
                  <span className="theme-toggle-thumb" />
                </span>
                <span className="theme-toggle-text">Retro</span>
              </label>
            </div>
          </div>
        </div>
      </header>

      <div className="main-content">
        <GrowthPanels
          poolData={poolData}
          lastBlockTime={validatorSnapshot.lastBlockTime}
          formatCurrency={formatCurrency}
          algoPrice={algoPrice}
        />

        {/* Key Metrics Cards */}
        <div className="metrics-grid">

          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon purple">💎</div>
              <div className="metric-values">
                <p className="metric-label">Blocks (1Y)</p>
                <p className="metric-value">{poolData?.performance?.total_blocks || 0}</p>
                <p className="metric-sub blue">Last 365 Days</p>
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon yellow">💰</div>
              <div className="metric-values">
                <p className="metric-label">APY (1Y)</p>
                <p className="metric-value">{poolData?.performance?.estimated_apy || '0.00'}%</p>
                <p className="metric-sub green">Last 365 Days</p>
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon orange">📈</div>
              <div className="metric-values">
                <p className="metric-label">APY (30D)</p>
                <p className="metric-value">{poolData?.performance?.estimated_apy_30d || '0.00'}%</p>
                <p className="metric-sub blue">Last 30 Days</p>
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <div className="metric-icon purple">⚡</div>
              <div className="metric-values">
                <p className="metric-label">APY (7D)</p>
                <p className="metric-value">{poolData?.performance?.estimated_apy_7d || '0.00'}%</p>
                <p className="metric-sub blue">Last 7 Days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Block Performance Section */}
        <div className="block-performance-section">
          <div className="block-metrics-grid">
            <div className="metric-card">
              <div className="metric-header">
                <div className="metric-icon blue">🧱</div>
                <div className="metric-values">
                  <p className="metric-label">Avg Blocks/Day (7D)</p>
                  <p className="metric-value">{(poolData?.performance?.avg_blocks_per_day_7d ?? 0).toFixed(2)}</p>
                  <p className="metric-sub blue">Last 7 Days</p>
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <div className="metric-icon green">🧱</div>
                <div className="metric-values">
                  <p className="metric-label">Avg Blocks/Day (30D)</p>
                  <p className="metric-value">{(poolData?.performance?.avg_blocks_per_day_30d ?? 0).toFixed(2)}</p>
                  <p className="metric-sub green">Last 30 Days</p>
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <div className="metric-icon orange">🧱</div>
                <div className="metric-values">
                  <p className="metric-label">Avg Blocks/Month</p>
                  <p className="metric-value">{(poolData?.performance?.avg_blocks_per_month ?? 0).toFixed(2)}</p>
                  <p className="metric-sub orange">1-year pace</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-grid">
          {/* Performance History Chart */}
          <div className="chart-card chart-card-pro">
            <div className="chart-card-header">
              <div className="chart-title-group">
                <span className="chart-eyebrow">30-day reward flow</span>
                <h3 className="chart-title blue">Daily Performance History</h3>
              </div>
              <div className="chart-header-value">
                <strong>{latestRewards !== null ? formatAverageValue(latestRewards, 2) : '0.00'}</strong>
                <span>Latest ALGO</span>
              </div>
            </div>
            <div className="chart-kpi-strip">
              <div className="chart-kpi">
                <span className="chart-kpi-label">7D avg</span>
                <strong>{rewardsLast7Average !== null ? `${formatAverageValue(rewardsLast7Average, 2)} ALGO` : 'N/A'}</strong>
              </div>
              <div className={`chart-kpi trend-${getTrendClass(rewardsTrendPct)}`}>
                <span className="chart-kpi-label">Vs prior 7D</span>
                <strong>{formatTrendPercent(rewardsTrendPct)}</strong>
              </div>
              <div className="chart-kpi">
                <span className="chart-kpi-label">Best day</span>
                <strong>{bestRewards !== null ? `${formatAverageValue(bestRewards, 2)} ALGO` : 'N/A'}</strong>
              </div>
              <div className="chart-kpi">
                <span className="chart-kpi-label">Latest to stakers</span>
                <strong>{latestStakerRewards !== null ? `${formatAverageValue(latestStakerRewards, 2)} ALGO` : 'N/A'}</strong>
              </div>
            </div>
            <div className="chart-container has-legend">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={normalizedDailyStats}
                  margin={{ top: 12, right: 18, left: -8, bottom: 6 }}
                  onClick={(chartState) => handleChartTap('rewards', chartState)}
                >
                  <defs>
                    <linearGradient id="colorRewards" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.8}/>
                      <stop offset="72%" stopColor="#2563eb" stopOpacity={0.22}/>
                      <stop offset="100%" stopColor="#0f172a" stopOpacity={0}/>
                    </linearGradient>
                    <filter id="chartGlow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#3b82f6" floodOpacity="0.28" />
                    </filter>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="4 8" stroke="rgba(148, 163, 184, 0.18)" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    fontSize={12}
                    tickFormatter={formatChartDate}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={22}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={12}
                    axisLine={false}
                    tickLine={false}
                    width={42}
                    tickFormatter={(value) => formatAverageValue(value, 0)}
                  />
                  <Tooltip 
                    content={renderRewardsTooltip}
                    cursor={{ stroke: 'rgba(96, 165, 250, 0.32)', strokeWidth: 1 }}
                  />
                  {averageRewards !== null && (
                    <ReferenceLine
                      y={averageRewards}
                      stroke={averageRewardsLineColor}
                      strokeDasharray="6 6"
                      strokeWidth={1.5}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="rewards_algo"
                    name="Gross rewards"
                    stroke="#60a5fa"
                    fillOpacity={1}
                    fill="url(#colorRewards)"
                    strokeWidth={2.5}
                    filter="url(#chartGlow)"
                    dot={false}
                    activeDot={{ r: 5, stroke: '#bfdbfe', strokeWidth: 2, fill: '#2563eb' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="staker_rewards_algo"
                    name="To stakers"
                    stroke="#34d399"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, stroke: '#bbf7d0', strokeWidth: 2, fill: '#059669' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-swatch legend-swatch-area" />
                <span className="legend-label">Gross daily rewards</span>
              </span>
              <span className="legend-item">
                <span className="legend-swatch legend-swatch-line" />
                <span className="legend-label">Net rewards to stakers</span>
              </span>
              {averageRewards !== null && (
                <span className="legend-item">
                  <span className="legend-swatch legend-swatch-average" />
                  <span className="legend-label">
                    Average: {formatAverageValue(averageRewards, 2)} ALGO
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Blocks Proposed Chart */}
          <div className="chart-card chart-card-pro">
            <div className="chart-card-header">
              <div className="chart-title-group">
                <span className="chart-eyebrow">Validator output</span>
                <h3 className="chart-title green">Daily Blocks Proposed</h3>
              </div>
              <div className="chart-header-value">
                <strong>{latestBlocks !== null ? formatAverageValue(latestBlocks, 0) : '0'}</strong>
                <span>Latest blocks</span>
              </div>
            </div>
            <div className="chart-kpi-strip">
              <div className="chart-kpi">
                <span className="chart-kpi-label">7D avg</span>
                <strong>{blocksLast7Average !== null ? formatAverageValue(blocksLast7Average, 2) : 'N/A'}</strong>
              </div>
              <div className={`chart-kpi trend-${getTrendClass(blocksTrendPct)}`}>
                <span className="chart-kpi-label">Vs prior 7D</span>
                <strong>{formatTrendPercent(blocksTrendPct)}</strong>
              </div>
              <div className="chart-kpi">
                <span className="chart-kpi-label">30D avg</span>
                <strong>{averageBlocks !== null ? formatAverageValue(averageBlocks, 2) : 'N/A'}</strong>
              </div>
              <div className="chart-kpi">
                <span className="chart-kpi-label">Best day</span>
                <strong>{bestBlocks !== null ? formatAverageValue(bestBlocks, 0) : 'N/A'}</strong>
              </div>
            </div>
            <div className="chart-container has-legend">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={normalizedDailyStats}
                  margin={{ top: 12, right: 18, left: -8, bottom: 6 }}
                  onClick={(chartState) => handleChartTap('blocks', chartState)}
                >
                  <defs>
                    <linearGradient id="blocksGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0.78}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="4 8" stroke="rgba(148, 163, 184, 0.18)" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    fontSize={12}
                    tickFormatter={formatChartDate}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={22}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={12}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    width={42}
                  />
                  <Tooltip 
                    content={renderBlocksTooltip}
                    cursor={{ fill: 'rgba(56, 189, 248, 0.08)' }}
                  />
                  <Bar
                    dataKey="blocks"
                    name="Blocks proposed"
                    fill="url(#blocksGradient)"
                    radius={[7, 7, 2, 2]}
                    maxBarSize={20}
                  />
                  {averageBlocks !== null && (
                    <ReferenceLine
                      y={averageBlocks}
                      stroke={averageBlocksLineColor}
                      strokeDasharray="6 6"
                      strokeWidth={1.5}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-swatch legend-swatch-bar" />
                <span className="legend-label">Daily blocks proposed</span>
              </span>
              {averageBlocks !== null && (
                <span className="legend-item">
                  <span className="legend-swatch legend-swatch-average" />
                  <span className="legend-label">
                    Average: {formatAverageValue(averageBlocks, 2)} blocks
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pool Summary Cards */}
        <div className="distribution-grid">
          <div className="chart-card">
            <h3 className="chart-title blue">Total Staked</h3>
            <div className="summary-content">
              <div className="summary-main">
                <div className="summary-value">{formatAverageValue(totalStaked / 1000000, 2)} ALGO</div>
              </div>
              <div className="summary-icon"></div>
            </div>
            <div className="summary-details" style={{ marginTop: '3rem' }}>
              <div className="detail-item">
                <span className="detail-label">ALGO Price ({currency})</span>
                <span className="detail-value">
                  {parsedAlgoPrice !== null ? formatCurrency(parsedAlgoPrice, 4) : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Pool Value ({currency})</span>
                <span className="detail-value">{formatCurrency(totalValueUsd)}</span>
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h3 className="chart-title green">Total Stakers</h3>
            <div className="summary-content">
              <div className="summary-main">
                <div className="summary-value">{totalStakers}</div>
                <div className="summary-sub">Active Participants</div>
              </div>
              <div className="summary-icon"></div>
            </div>
            <div className="summary-details">
              <div className="detail-item">
                <span className="detail-label">Average Stake</span>
                <span className="detail-value">{formatNumber(avgStake)} ALGO</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Avg Value ({currency})</span>
                <span className="detail-value">{formatCurrency(avgStake * algoPrice)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Individual Staker Earnings Table */}
        <div className="table-card">
          <div className="table-header">
            <h3 className="table-title">Individual Staker Earnings</h3>
            <div className="table-controls">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search by address or NFD..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <span className="search-icon"><Search size={16} /></span>
              </div>
            </div>
          </div>

          <div className="staker-insights" aria-label="Staker rewards summary">
            <div className="staker-insight-card">
              <span className="staker-insight-label">Net Staker APY (30D)</span>
              <span className="staker-insight-value">{formatAverageValue(netStakerApy30d, 2)}%</span>
              <span className="staker-insight-sub">
                After 1% commission{grossApy30d !== null ? `, ${formatAverageValue(grossApy30d, 2)}% gross` : ''}
              </span>
            </div>
            <div className="staker-insight-card">
              <span className="staker-insight-label">Daily To Stakers</span>
              <span className="staker-insight-value">{formatAlgoAmount(dailyStakerRewardsAlgo, 2)} ALGO</span>
              <span className="staker-insight-sub">{formatCurrency(dailyStakerRewardsUsd)}</span>
            </div>
            <div className="staker-insight-card">
              <span className="staker-insight-label">Lifetime Earned</span>
              <span className="staker-insight-value">
                {stakerListIsEmpty ? 'Syncing' : `${formatAlgoAmount(totalEarnedAlgo, 2)} ALGO`}
              </span>
              <span className="staker-insight-sub">
                {stakerListIsEmpty ? 'Waiting for staker boxes' : formatCurrency(totalEarnedAlgo * algoPrice)}
              </span>
            </div>
          </div>

          {(stakerDataIsStale || stakerListIsEmpty) && (
            <div className={`staker-sync-notice${stakerDataIsStale ? ' stale' : ''}`} role="status">
              <Info size={16} />
              <span>{stakerSyncMessage}</span>
            </div>
          )}

          <div className="staker-count">
            {stakerListIsEmpty ? 'Staker list syncing...' : `${sortedStakers.length} stakers`}
            {!stakerListIsEmpty && (
              <span className="staker-estimate-note">
                Estimates use the 30D block reward pace and each wallet's pool share: day x 7 / 30 / 365. Actual rewards can move with block luck.
              </span>
            )}
          </div>

          <div className="table-container">
            <table className="stakers-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('address')}>
                    Address {sortConfig.key === 'address' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('balance')}>
                    Staked ALGO {sortConfig.key === 'balance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('pool_pct')}>
                    Pool % {sortConfig.key === 'pool_pct' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('daily_rewards')}>
                    Est. / Day {sortConfig.key === 'daily_rewards' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('weekly_rewards')}>
                    Est. 7D {sortConfig.key === 'weekly_rewards' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('monthly_rewards')}>
                    Est. 30D {sortConfig.key === 'monthly_rewards' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('yearly_rewards')}>
                    Est. 1Y {sortConfig.key === 'yearly_rewards' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('total_earned')}>
                    Total Earned {sortConfig.key === 'total_earned' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Stake APY</th>
                </tr>
              </thead>
              <tbody>
                {stakerListIsEmpty ? (
                  <tr>
                    <td colSpan="9">
                      <div className="staker-empty-state">
                        <Info size={18} />
                        <span>{stakerSyncMessage}</span>
                      </div>
                    </td>
                  </tr>
                ) : sortedStakers.map((staker, index) => {
                  const isOwnerAddress = staker.address === 'MPSNWRHCKQNJ2LJ3XEBFDVI6BGVXB2BWIGTOFTXOGSGS4T6EIYUEOS5S4M';
                  const isValidatorRow = Boolean(staker.is_validator || isOwnerAddress);
                  const stakeAmountAlgo = Number(staker.staked_amount_algo) || 0;
                  const fullStakeAmountLabel =
                    formatAverageValue(stakeAmountAlgo, 2) || stakeAmountAlgo.toFixed(2);
                  const dailyRewardsValue = firstFiniteNumber(
                    staker.estimated_rewards_per_day_algo,
                    staker.daily_rewards_algo,
                    staker.daily_staking_rewards_algo,
                  );
                  const weeklyRewardsValue = firstFiniteNumber(
                    staker.estimated_rewards_7d_algo,
                    staker.staking_rewards_7d_algo,
                    staker.rewards_7d_algo,
                    staker.weekly_rewards_algo,
                    dailyRewardsValue * 7,
                  );
                  const monthlyRewardsValue = firstFiniteNumber(
                    staker.estimated_rewards_30d_algo,
                    staker.staking_rewards_30d_algo,
                    staker.rewards_30d_algo,
                    staker.monthly_rewards_algo,
                    dailyRewardsValue * 30,
                  );
                  const yearlyRewardsValue = firstFiniteNumber(
                    staker.estimated_rewards_1y_algo,
                    staker.yearly_staking_rewards_algo,
                    staker.yearly_rewards_algo,
                    dailyRewardsValue * 365,
                  );
                  const totalEarnedValue = firstFiniteNumber(staker.total_earned_algo);
                  const totalEarnedUsd = firstFiniteNumber(
                    staker.total_earned_usd,
                    totalEarnedValue * algoPrice,
                  );
                  const totalEarnedDisplay = formatAlgoAmount(totalEarnedValue, 2);
                  const apyValue = firstFiniteNumber(staker.estimated_apy_30d, staker.estimated_apy);
                  const renderEstimateCell = (stakeValue) => {
                    return (
                      <div className="value-cell">
                        <div className="primary-value">{formatAlgoAmount(stakeValue, 2)} ALGO</div>
                        <div className="secondary-value">{formatCurrency(stakeValue * algoPrice)}</div>
                      </div>
                    );
                  };
                  return (
                  <tr key={staker.address} className={isValidatorRow ? 'owner-row' : ''}>
                    <td>
                      <div className="address-cell">
                        {(() => {
                          const nfd = nfdCache[staker.address];
                          const isAddressRevealed = Boolean(revealedAddresses[staker.address]);
                          const shortAddress = `${staker.address.slice(0, 8)}...${staker.address.slice(-8)}`;
                          const visibleLabel = isAddressRevealed
                            ? (nfd || shortAddress)
                            : (isValidatorRow ? 'Validator' : `Staker #${index + 1}`);

                          return (
                            <div className="staker-address-actions">
                              <button
                                type="button"
                                className={`staker-address-toggle${isAddressRevealed ? ' is-revealed' : ''}`}
                                onClick={() => {
                                  setRevealedAddresses((prev) => ({
                                    ...prev,
                                    [staker.address]: !prev[staker.address],
                                  }));
                                }}
                                title={isAddressRevealed ? 'Hide wallet address' : 'Show wallet address'}
                                aria-label={isAddressRevealed ? 'Hide wallet address' : `Show wallet address for ${visibleLabel}`}
                              >
                                {visibleLabel}
                              </button>
                              {isAddressRevealed && (
                                <a
                                  href={`https://allo.info/account/${staker.address}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="address-external-link"
                                  title={nfd ? `NFD: ${nfd} - View on allo.info` : 'View on allo.info'}
                                  aria-label="View wallet on allo.info"
                                >
                                  <ExternalLink size={14} />
                                </a>
                              )}
                              {isValidatorRow && (
                                <span className="validator-badge" title="Validator">
                                  VALIDATOR
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className={`value-cell stake-amount-cell${isMobileView ? ' is-mobile' : ''}`}>
                        <button
                          type="button"
                          className="stake-amount-button"
                          onClick={() => handleStakeAmountClick(staker)}
                          title={`${fullStakeAmountLabel} ALGO`}
                          aria-label="View full staked amount"
                        >
                          <div className="primary-value">{formatNumber(stakeAmountAlgo)} ALGO</div>
                          <div className="secondary-value">{formatCurrency(staker.staked_amount_usd)}</div>
                        </button>
                        {isMobileView && stakeAmountPopup?.address === staker.address && (
                          <div
                            className="stake-popup-backdrop"
                            role="presentation"
                            onClick={() => setStakeAmountPopup(null)}
                          >
                            <div
                              className={`stake-popup${isValidatorRow ? ' is-validator' : ''}`}
                              role="dialog"
                              aria-modal="true"
                              aria-label={`Full staked amount for ${isValidatorRow ? 'Validator' : `Staker #${index + 1}`}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="stake-popup-close"
                                onClick={() => setStakeAmountPopup(null)}
                                aria-label="Close full stake details"
                              >
                                ×
                              </button>
                              <span className="stake-popup-owner">
                                {isValidatorRow ? 'Validator' : `Staker #${index + 1}`}
                              </span>
                              <span className="stake-popup-label">Full staked balance</span>
                              <span className="stake-popup-value">{fullStakeAmountLabel}</span>
                              <span className="stake-popup-unit">ALGO</span>
                              <span className="stake-popup-sub">{formatCurrency(staker.staked_amount_usd)}</span>
                              <span className="stake-popup-share">{staker.stake_percentage}% of pool stake</span>
                              <span className="stake-popup-hint">Tap outside or use × to close</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="percentage-value">{staker.stake_percentage}%</span>
                    </td>
                    <td>
                      {renderEstimateCell(dailyRewardsValue)}
                    </td>
                    <td>
                      {renderEstimateCell(weeklyRewardsValue)}
                    </td>
                    <td>
                      {renderEstimateCell(monthlyRewardsValue)}
                    </td>
                    <td>
                      {renderEstimateCell(yearlyRewardsValue)}
                    </td>
                    <td>
                      <div className="value-cell">
                        <div className="primary-value">{totalEarnedDisplay} ALGO</div>
                        <div className="secondary-value">{formatCurrency(totalEarnedUsd)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="apy-container">
                        <span className="apy-value">{formatAverageValue(apyValue, 2)}%</span>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-text-section">
            <p className="footer-text">
              © {new Date().getFullYear()} PoorSlugsPool. Real-time Algorand staking analytics.
            </p>
            <p className="footer-attribution">
              Data provided by{' '}
              <a 
                href="https://nodely.io/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="nodely-link"
              >
                Nodely
              </a>
            </p>
          </div>
          <div className="footer-status">
            <span>Data updated: {poolDataUpdatedLabel}</span>
            <div className="status-indicator">
              <div className="status-dot"></div>
              <span>Connected</span>
            </div>
          </div>
        </div>
      </footer>

      {showFaqPopup && <FaqPopup poolData={poolData} onClose={closeFaqPopup} />}
      {showSwapPopup && <SwapPopup onClose={closeSwapPopup} />}

      {/* How to Stake Popup */}
      {showHowToPopup && (
        <div className="popup-overlay" onClick={() => setShowHowToPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2 className="popup-title">How to Stake ALGO on Reti Pool</h2>
              <button 
                className="popup-close-btn" 
                onClick={() => setShowHowToPopup(false)}
                aria-label="Close popup"
              >
                &times;
              </button>
            </div>
            
            <div className="popup-body">
              <p className="popup-intro">
                Want to earn rewards on your Algorand (ALGO) while helping secure the network? Here's the step-by-step:
              </p>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">1</span>
                  <h3 className="step-title">Buy ALGO on an Exchange</h3>
                </div>
                <div className="step-content">
                  <p>Create an account on a centralized exchange (Coinbase, Binance, Kraken, etc.).</p>
                  <p>Buy at least 1,005 ALGO (minimum staking amount is 1,000 ALGO, but extra covers network fees).</p>
                </div>
              </div>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">2</span>
                  <h3 className="step-title">Send ALGO to Your Wallet</h3>
                </div>
                <div className="step-content">
                  <p>Download a wallet that supports Algorand (Defly, Pera, Lute, Exodus, etc.).</p>
                  <p>Copy your wallet's ALGO address.</p>
                  <p>Withdraw ALGO from the exchange, paste your wallet address, then confirm.</p>
                  <p>Wait 1-2 minutes for the transfer to complete.</p>
                </div>
              </div>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">3</span>
                  <h3 className="step-title">Go to Reti Pool</h3>
                </div>
                <div className="step-content">
                  <a href="https://reti.nodely.io/validators/19" target="_blank" rel="noopener noreferrer" className="popup-action-link">
                    <span>Open Poorslugspool on Reti</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">4</span>
                  <h3 className="step-title">Connect Your Wallet</h3>
                </div>
                <div className="step-content">
                  <p>Click "Connect Wallet".</p>
                  <p>Choose your wallet (Defly, Pera, Lute, Exodus, WalletConnect, etc.).</p>
                  <p>Approve the connection in your wallet.</p>
                  <div className="wallet-tip">
                    <div className="wallet-tip-icon">
                      <Smartphone size={20} />
                    </div>
                    <div className="wallet-tip-content">
                      <strong>Pera mobile tip</strong>
                      <p>Use Safari or Chrome instead of a social app browser, keep Pera updated, and open Pera once before tapping Connect Wallet.</p>
                      <p>If Pera loops or stalls, disconnect old sessions inside Pera, clear site data for reti.nodely.io, then reconnect from the Reti validator page.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">5</span>
                  <h3 className="step-title">Stake Your ALGO</h3>
                </div>
                <div className="step-content">
                  <p>Enter the amount you want to stake (minimum 1,000 ALGO, leave a few for fees).</p>
                  <p>Confirm the transaction in your wallet.</p>
                  <p>Your ALGO is now delegated to the Reti validator.</p>
                </div>
              </div>

              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">6</span>
                  <h3 className="step-title">Track Rewards</h3>
                </div>
                <div className="step-content">
                  <p>Click Refresh on the validator page.</p>
                  <p>Your staked balance and rewards will appear.</p>
                  <p>Rewards accumulate automatically; no claiming needed.</p>
                </div>
              </div>

              <div className="popup-summary">
                <div className="summary-icon"><Info size={22} /></div>
                <div className="summary-text">
                  <strong>That's it: buy about 1,005+ ALGO, send it to your wallet, connect on Reti, stake at least 1,000 ALGO, and refresh.</strong>
                  <br />
                  You're now staking with Reti Pool and earning rewards!
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
